import dayjs from "dayjs";
import { AudioLines } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FaPlay, FaYoutube } from "react-icons/fa";
import { addToCurrentAudio, changeIsAudioPlayState, changeIsPlayingState } from "~/lib/store/slice/audioSlice";
import { onPauseVideo } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import PdfIcon from "~/svg/PdfIcon";
import Points from "~/svg/Points";
import { FALLBACK_IMAGE_URL, youtubeLinkPrefix } from "~/utils/constant";

const QuizInfoCard = ({
  id,
  title,
  date,
  time,
  points,
  source_type,
  source_path,
  from,
  onClick,
  className,
  isPlayed = false,
}: {
  id: number;
  title: string;
  date?: string;
  time?: string;
  points?: number;
  source_type?: "pdf" | "yt_video" | "audio";
  source_path?: string;
  from: "upcoming_quiz" | "previous_quiz";
  isPlayed?: boolean;
  onClick?: () => void;
  className?: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const get12HouresFormat = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    return `${formattedHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const handleOnAudioClick = ({ audio_path }: { audio_path: string }) => {
    dispatch(onPauseVideo());
    const currentAudio = {
      id: id,
      img: FALLBACK_IMAGE_URL,
      totalDuration: null,
      artist: null,
      name: title,
      audio: audio_path,
      from: "satsang_quiz",
    };
    dispatch(changeIsAudioPlayState(true));
    dispatch(changeIsPlayingState(true));
    dispatch(addToCurrentAudio(currentAudio));
  };
  return (
    <div
      className={`w-auto flex justify-between items-center bg-cardBg border border-borderPrimary rounded-lg p-4 relative ${className}`}
      onClick={() => {
        onClick?.();
      }}
    >
      <div className="flex flex-col justify-start items-start gap-0.5 flex-1">
        <span className="text-lg text-textColor font-baloobhai2Medium">{title}</span>

        <div className="flex justify-start items-center text-14 gap-2 text-textSecondaryColor font-inter flex-wrap">
          {date && <p>{dayjs(date).format("DD-MMM, YYYY")}</p>}
          {time && <p className="px-2 border-l border-textSecondaryColor">{get12HouresFormat(time)}</p>}
          {points && (
            <p className="text-textHeadingColor px-2 border-l border-textSecondaryColor flex gap-1">
              <Points /> {points} {t("common.points", { defaultValue: "Points" })}
            </p>
          )}
          {isPlayed && (
            <div className={cn("gap-x-1 text-textHeadingColor px-2 border-l  border-textSecondaryColor flex gap-1")}>
              <span className="bg-primaryColor text-white px-2 rounded-full body_text_s">{t("common.played", { defaultValue: "Played" })}</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-center items-center gap-2">
        {from === "upcoming_quiz" && (
          <>
            {source_type === "pdf" && (
              <a
                href={source_path}
                target="_blank"
                rel="noopener noreferrer"
                className="size-7 rounded-full bg-darkOrangeColor flex justify-center items-center cursor-pointer"
                title="PDF"
              >
                <PdfIcon fill="white" />
              </a>
            )}
            {source_type === "yt_video" && (
              <a
                href={`${youtubeLinkPrefix}${source_path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="size-7 rounded-full bg-darkOrangeColor flex justify-center items-center cursor-pointer"
                title="Youtube"
              >
                <FaYoutube size={16} color="white" />
              </a>
            )}
            {source_type === "audio" && (
              <div
                className="size-7 rounded-full bg-darkOrangeColor flex justify-center items-center cursor-pointer"
                title="Audio"
                onClick={() => handleOnAudioClick({ audio_path: source_path ?? "" })}
              >
                <AudioLines size={16} color="white" />
              </div>
            )}
          </>
        )}
        {from === "previous_quiz" && (
          <div className="size-7 rounded-full bg-darkOrangeColor flex justify-center items-center cursor-pointer" title="Play">
            <FaPlay size={10} color="white" />
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizInfoCard;
